import {
  Flex,
  IconButton,
  IconEditLine,
  IconExternalLinkLine,
  IconSyllabusLine,
  IconSyllabusSolid,
  IconTrashLine,
  Link,
  ScreenReaderContent,
  Text,
  ToggleGroup,
  Tooltip,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ProgramService } from "../../services/ProgramService";
import { sort } from "../../utils/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo/DialogYesNo";
import ProgramsModal from "../Programs/ProgramsModal";
import { destructureAccreditor } from "../Programs/Utils";
import CourseGroup from "./CourseGroup";

const Program = ({ program, parent, setParent, editPermission }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [programData, setProgramData] = useState(program);
  const [loading, setLoading] = useState(false);
  const [removeProgramDialogOpen, setRemoveProgramDialogOpen] = useState(false);
  const [openEditProgram, setOpenEditProgram] = useState(false);
  const [programExpanded, setProgramExpanded] = useState(false);

  const handleOnToggle = (expanded) => {
    setProgramExpanded(expanded);
    if (!programData.courseGroups) {
      setLoading(true);
      ProgramService.getCourseGroupsByProgram(program.id)
        .then((cg) => {
          cg.course_groups = sort(cg.course_groups, (cg) => cg.name);
          setProgramData((prevData) => ({
            ...prevData,
            courseGroups: cg.course_groups,
          }));
          setLoading(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("programs.courseGroups"),
            }),
            ERROR
          );
        });
    }
  };

  const handleRemoveProgram = async () => {
    await ProgramService.deleteProgram(programData.id)
      .then(() => {
        setParent((prevParent) => ({
          ...prevParent,
          programs: prevParent.programs.filter((p) => p.id !== programData.id),
        }));
        setRemoveProgramDialogOpen(false);
        showNotification(
          t("notifications.general.deleteSuccess", {
            obj: t("programs.program"),
          })
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", {
            obj: t("programs.program"),
          }),
          ERROR
        );
      });
  };

  const handleEditProgram = async (program) => {
    try {
      const newProgram = await ProgramService.editProgram(program);
      const cg = await ProgramService.getCourseGroupsByProgram(program.id);

      setProgramData({ ...newProgram, courseGroups: cg.course_groups });
      setOpenEditProgram(false);
      setProgramExpanded(true);
      showNotification(
        t("notifications.general.updateSuccess", { obj: t("programs.program") })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", { obj: t("programs.program") }),
        ERROR
      );
    }
  };

  return (
    <>
      <ToggleGroup
        size="small"
        border={false}
        toggleLabel={t("academicHierarchy.toggleProgram")}
        iconExpanded={IconSyllabusSolid}
        icon={IconSyllabusLine}
        expanded={programExpanded}
        onToggle={(_e, expanded) => handleOnToggle(expanded)}
        summary={
          <Flex alignItems="center" gap="small">
            <Flex.Item shouldShrink>
              <Text>{programData.degreeName}</Text>
            </Flex.Item>
            {programData.accreditor.split("-")[0] && (
              <Flex.Item>
                <Flex alignItems="center" gap="x-small" wrap="wrap">
                  <Flex.Item>
                    <Text>
                      <Text weight="bold">{t("programs.accreditor")}: </Text>
                      {programData.accreditor.split("-")[0]}
                    </Text>
                  </Flex.Item>
                  {programData.accreditor.split("-")[1] && (
                    <Flex.Item>
                      <Tooltip
                        renderTip={t("programs.accreditorTooltip", {
                          programAccreditorUrl: destructureAccreditor(
                            programData.accreditor
                          )[0],
                        })}
                        on={["hover", "focus"]}
                        color="primary-inverse"
                        placement="top"
                      >
                        <Link
                          href={
                            destructureAccreditor(programData.accreditor)[1]
                          }
                          target="_blank"
                        >
                          <IconExternalLinkLine size="x-small" />
                          <ScreenReaderContent>
                            {t("programs.accreditorTooltip", {
                              programAccreditorUrl: destructureAccreditor(
                                programData.accreditor
                              )[0],
                            })}
                          </ScreenReaderContent>
                        </Link>
                      </Tooltip>
                    </Flex.Item>
                  )}
                </Flex>
              </Flex.Item>
            )}
            {editPermission && (
              <Flex.Item shouldGrow textAlign="end">
                <Tooltip
                  color="primary-inverse"
                  renderTip={t("academicHierarchy.editProgram")}
                >
                  <IconButton
                    id="edit-program"
                    renderIcon={IconEditLine}
                    screenReaderLabel={t("academicHierarchy.editProgram")}
                    margin="0 small 0 0"
                    onClick={() => setOpenEditProgram(true)}
                  />
                </Tooltip>
                <Tooltip
                  color="primary-inverse"
                  renderTip={t("academicHierarchy.removeProgram")}
                >
                  <IconButton
                    id="remove-program"
                    renderIcon={IconTrashLine}
                    screenReaderLabel={t("academicHierarchy.removeProgram")}
                    onClick={() => setRemoveProgramDialogOpen(true)}
                  />
                </Tooltip>
              </Flex.Item>
            )}
          </Flex>
        }
      >
        <div style={{ paddingLeft: 12 }}>
          {!loading ? (
            programData.courseGroups && programData.courseGroups.length > 0 ? (
              programData.courseGroups.map((cg) => (
                <CourseGroup
                  key={cg.id}
                  courseGroup={cg}
                  editPermission={editPermission}
                />
              ))
            ) : (
              <div style={{ marginLeft: 25, padding: 10 }}>
                {t("academicHierarchy.noCourseGroups")}
              </div>
            )
          ) : (
            <Skeleton style={{ height: 64, borderRadius: 5 }} />
          )}
        </div>
      </ToggleGroup>
      <ProgramsModal
        open={openEditProgram}
        setOpen={setOpenEditProgram}
        department={parent}
        title={t("academicHierarchy.editProgram")}
        service={handleEditProgram}
        program={programData}
        setProgram={setProgramData}
      />
      <DialogYesNo
        message={t("programs.areYouSure")}
        openStatus={removeProgramDialogOpen}
        onClickNo={() => setRemoveProgramDialogOpen(false)}
        onClickYes={handleRemoveProgram}
      />
    </>
  );
};

export default Program;
